/* eslint-disable react/jsx-props-no-spreading -- the drag handlers of a day are a set of event props */
import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import type { CalendarDay } from './calendarDates';
import { CalendarDayContent } from './calendarDayModel';
import type { CalendarGridProps } from './calendarGridTypes';
import { calendarMessages } from './calendarMessages';
import { CalendarPostCard } from './CalendarPostCard';
import { ReleaseBand } from './ReleaseBand';
import { RolloutStepMark } from './RolloutStepMark';
import { AddOnDayMenu } from './AddOnDayMenu';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** A day shows at most this many cards, then "+N more" (design a1). */
export const MONTH_CELL_CARDS = 3;

interface MonthDayCellProps extends Omit<CalendarGridProps, 'days' | 'contentByDay'> {
  day: CalendarDay;
  content: CalendarDayContent;
}

/**
 * One day of the Month grid (design a1): the day number, the rollout steps of the day, the release
 * band, up to three cards and "+N more". A day of another month is faint. While a card is held,
 * a day before today is hatched and refuses the drop (design b6).
 */
const MonthDayCellComponent: React.FC<MonthDayCellProps> = ({ day, content, ...grid }) => {
  const intl = useIntl();
  const { drag, songs, onOpenDay, onOpenPost, slotsFor, onAdd, selectedUuid, selectedDayKey } = grid;
  const shown = useMemo(() => content.posts.slice(0, MONTH_CELL_CARDS), [content.posts]);
  const hidden = content.posts.length - shown.length;
  const slots = useMemo(() => slotsFor(day.start, content.posts), [content.posts, day.start, slotsFor]);
  const openDay = useCallback(() => onOpenDay(day.key), [day.key, onOpenDay]);
  const isTarget = drag.isHolding && drag.targetDayKey === day.key;
  const tasks = useMemo(() => content.steps.filter((step) => step.isTask), [content.steps]);
  const marks = useMemo(() => content.steps.filter((step) => !step.isTask), [content.steps]);
  const dayNumber =
    day.start.getDate() === 1 ? intl.formatDate(day.start, { month: 'short', day: 'numeric' }) : day.start.getDate();

  return (
    <div
      className={clsx(
        styles.dc,
        !day.isInPeriod && styles.dcOut,
        day.isToday && styles.dcToday,
        day.isPast && styles.dcPast,
        selectedDayKey === day.key && styles.dcSelected,
        drag.isHolding && day.isPast && styles.dcNoDrop,
        isTarget && !day.isPast && styles.dcDrop
      )}
      data-testid={`month-day-${day.key}`}
      {...drag.dayHandlers(day.key)}
    >
      <div className={styles.dhead}>
        <button
          type="button"
          className={styles.dnum}
          aria-label={intl.formatDate(day.start, { weekday: 'long', day: 'numeric', month: 'long' })}
          onClick={openDay}
        >
          {dayNumber}
        </button>
        {marks.map((step) => (
          <RolloutStepMark key={step.rolloutKey} step={step} song={songs.get(step.catalogItemUuid)} />
        ))}
        {day.isPast ? null : <AddOnDayMenu day={day.start} slots={slots} onPick={onAdd} />}
      </div>
      {content.releases.map((song) => (
        <ReleaseBand key={song.catalogItemUuid} song={song} />
      ))}
      {tasks.map((step) => (
        <RolloutStepMark key={step.rolloutKey} step={step} song={songs.get(step.catalogItemUuid)} />
      ))}
      {shown.map((post) => (
        <CalendarPostCard
          key={post.uuid}
          post={post}
          song={post.catalogItemUuid === null ? undefined : songs.get(post.catalogItemUuid)}
          drag={drag}
          isSelected={selectedUuid === post.uuid}
          onOpen={onOpenPost}
        />
      ))}
      {hidden > 0 ? (
        <button
          type="button"
          className={styles.more}
          aria-label={intl.formatMessage(calendarMessages.moreCount, { count: hidden })}
          onClick={openDay}
        >
          {/* A narrow cell shows "+3" (PromoteCalendar.module.scss); the name keeps "+3 more". */}
          <span className={styles.moreLong} aria-hidden="true">
            {intl.formatMessage(calendarMessages.moreCount, { count: hidden })}
          </span>
          <span className={styles.moreShort} aria-hidden="true">
            {intl.formatMessage(calendarMessages.moreCountShort, { count: hidden })}
          </span>
        </button>
      ) : null}
    </div>
  );
};

export const MonthDayCell = TypedMemo(MonthDayCellComponent);
